
import React, { useState, useMemo } from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import {
    X,
    Clock,
    Shield,
    Zap,
    AlertCircle,
    CheckCircle2,
    Plug,
    Calendar,
    MessageSquare,
    Send,
    Trash2,
    Loader2, Info
} from 'lucide-react';
import { format } from 'date-fns';
import { Task, ExternalTask } from '../types';
import AlertModal from './AlertModal';
import CustomSelect, { SelectOption } from './CustomSelect';

// --- CONFIG ---
const EDIT_ENABLED = true; // Toggle this to enable/disable inline editing globally in this view

interface TaskDetailViewProps {
    taskId: string;
    onClose?: () => void;
    editable?: boolean; // NEW: Allow override per instance
}

const TaskDetailView: React.FC<TaskDetailViewProps> = ({ taskId, onClose, editable = false }) => {
    const isEditAllowed = EDIT_ENABLED || editable;
    const { 
        tasks,
        allUsers, 
        getTeamName, 
        getTeamColor, 
        addTaskComment,
        updateTaskStatus,
        deleteTaskComment,
        externalIdentities,
        updateTask,
        getDependencies,
        userProfile,
        showToast
    } = useStandUp();

    const [commentText, setCommentText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUpdatingAssignee, setIsUpdatingAssignee] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false); // NEW
    const [isUpdatingPriority, setIsUpdatingPriority] = useState(false); // NEW
    const [isEditingTitle, setIsEditingTitle] = useState(false); // NEW
    const [isEditingDesc, setIsEditingDesc] = useState(false); // NEW
    const [editedTitle, setEditedTitle] = useState(''); // NEW
    const [editedDesc, setEditedDesc] = useState(''); // NEW
    const [isSavingTitle, setIsSavingTitle] = useState(false); // NEW
    const [isSavingDesc, setIsSavingDesc] = useState(false); // NEW
    const [commentToDelete, setCommentToDelete] = useState<{taskId: string, commentId: string} | null>(null);

    const statusOptions: SelectOption[] = [
        { id: 'todo', label: 'Todo' },
        { id: 'in-progress', label: 'In Progress' },
        { id: 'deep-work', label: 'Deep Work' },
        { id: 'blocked', label: 'Blocked' },
        { id: 'done', label: 'Done' }
    ];

    const priorityOptions: SelectOption[] = [
        { id: 'low', label: 'Low' },
        { id: 'medium', label: 'Medium' },
        { id: 'high', label: 'High' },
        { id: 'critical', label: 'Critical' }
    ];

    const handleStatusChange = async (newStatus: any) => {
        if (!task || isUpdatingStatus) return;
        setIsUpdatingStatus(true);
        try {
            await updateTaskStatus(task.id, newStatus);
            showToast(`Status updated to ${newStatus.replace('-', ' ')}`);
        } catch (e) {
            console.error(e);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handlePriorityChange = async (newPriority: any) => {
        if (!task || isUpdatingPriority) return;
        setIsUpdatingPriority(true);
        try {
            await updateTask(task.id, { priority: newPriority });
            showToast(`Priority updated to ${newPriority}`);
        } catch (e) {
            console.error(e);
        } finally {
            setIsUpdatingPriority(false);
        }
    };

    const handleSaveTitle = async () => {
        if (!task || !editedTitle.trim() || editedTitle === task.title || isSavingTitle) {
            setIsEditingTitle(false);
            return;
        }
        setIsSavingTitle(true);
        try {
            await updateTask(task.id, { title: editedTitle.trim() });
            showToast("Title updated");
            setIsEditingTitle(false);
        } catch (e) { 
            console.error(e); 
            showToast("Failed to update title", "error");
        } finally {
            setIsSavingTitle(false);
        }
    };

    const handleSaveDesc = async () => {
        if (!task || editedDesc === (task.description || '') || isSavingDesc) {
            setIsEditingDesc(false);
            return;
        }
        setIsSavingDesc(true);
        try {
            await updateTask(task.id, { description: editedDesc.trim() });
            showToast("Description updated");
            setIsEditingDesc(false);
        } catch (e) { 
            console.error(e); 
            showToast("Failed to update description", "error");
        } finally {
            setIsSavingDesc(false);
        }
    };

    // REACTIVE TASK: Find the latest version from context stream
    const task = useMemo(() => {
        return (tasks as (Task | ExternalTask)[]).find(t => t.id === taskId) || null;
    }, [tasks, taskId]);

    const assigneeOptions: SelectOption[] = useMemo(() => {
        if (!task) return [];
        
        const isExternal = 'externalSource' in task;
        const teamId = (task as any).teamId;
        const provider = isExternal ? (task as ExternalTask).externalSource : null;
        
        const filtered = allUsers.filter(u => {
            if (isExternal && provider) {
                if (provider === 'hrm') return true;
                return true; 
            }
            return u.teamIds?.includes(teamId);
        });

        const finalFiltered = filtered.filter(u => {
            if (isExternal && provider && provider !== 'hrm') {
                const identity = (externalIdentities as any)[u.email?.toLowerCase() || ''];
                return !!identity?.mappings?.[provider];
            }
            return true;
        });
        
        const options: SelectOption[] = [
            { id: '', label: 'Unassigned' }
        ];

        finalFiltered.forEach(u => {
            options.push({
                id: u.id,
                label: u.displayName,
                image: u.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.displayName)}`
            });
        });

        if (task.assigneeId && !options.some(o => o.id === task.assigneeId)) {
            const current = allUsers.find(u => u.id === task.assigneeId);
            if (current) {
                options.push({
                    id: current.id,
                    label: current.displayName,
                    image: current.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(current.displayName)}`
                });
            }
        }
        
        return options;
    }, [allUsers, task, externalIdentities]);

    const sortedComments = useMemo(() => {
        if (!task?.comments) return [];
        return [...task.comments].sort((a, b) => b.createdAt - a.createdAt);
    }, [task?.comments]);

    const formatDateSafe = (time: any, formatStr: string = 'h:mm a') => {
        if (!time) return '...';
        try {
            const date = typeof time.toDate === 'function' ? time.toDate() : new Date(time);
            if (isNaN(date.getTime())) return '...';
            return format(date, formatStr);
        } catch (e) {
            return '...';
        }
    };

    if (!task) return (
        <div className="flex-1 flex flex-col items-center justify-center p-12 text-gray-400">
            <Info size={48} className="mb-4 opacity-20" />
            <p className="text-sm italic">Task not found or has been deleted.</p>
        </div>
    );

    const isExternal = 'externalSource' in task;
    const handleAssigneeChange = async (newId: string) => {
        if (!task || isUpdatingAssignee) return;
        setIsUpdatingAssignee(true);
        try {
            await updateTask(task.id, { assigneeId: newId });
        } catch (e) {
            console.error("[Task Error] Quick reassign failed", e);
        } finally {
            setIsUpdatingAssignee(false);
        }
    };
    
    const teamName = !isExternal ? getTeamName((task as Task).teamId) : 'External Project';
    const teamColor = !isExternal ? getTeamColor((task as Task).teamId) : { style: 'bg-indigo-100 text-indigo-800', dot: 'bg-indigo-500' };

    const handleAddComment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!commentText.trim() || isSubmitting) return;
        
        setIsSubmitting(true);
        try {
            await addTaskComment(task.id, commentText);
            setCommentText('');
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDeleteComment = async () => {
        if (!commentToDelete) return;
        try {
            await deleteTaskComment(commentToDelete.taskId, commentToDelete.commentId);
        } catch (e) {
            console.error(e);
        } finally {
            setCommentToDelete(null);
        }
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-900">
            {/* Header / Banner */}
            <div className={`h-2 w-full ${teamColor.dot} shrink-0`}></div>
            
            <div className="flex-1 overflow-y-auto">
                <div className="p-6 space-y-6">
                    {/* Title Section */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {(task.taskKey || (task as any).externalId) && (
                                    <span 
                                        onClick={() => {
                                            const id = task.taskKey || (task as any).externalId || '';
                                            const textToCopy = `${id}: ${task.title}`;
                                            navigator.clipboard.writeText(textToCopy);
                                            showToast(`${id} copied!`);
                                        }}
                                        className={`px-2 py-0.5 rounded text-[10px] font-mono font-black uppercase tracking-tighter border shadow-sm select-all cursor-pointer active:scale-90 transition-all ${
                                            task.taskKey 
                                            ? 'bg-brand-500 text-white border-brand-600'
                                            : 'bg-indigo-600 text-white border-indigo-700'
                                        }`} 
                                        title="Click to copy ID and Title"
                                    >
                                        {task.taskKey || `${(task as any).externalSource === 'openproject' ? '#' : ''}${(task as any).externalId}`}
                                    </span>
                                )}
                                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-[10px] font-bold text-gray-500 uppercase tracking-wider border border-gray-200 dark:border-gray-700">
                                    {teamName}
                                </span>
                                {isExternal && (
                                    <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 rounded text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider border border-indigo-100 dark:border-indigo-800 flex items-center gap-1">
                                        <Plug size={10} /> {(task as any).externalSource}
                                    </span>
                                )}
                            </div>
                            {onClose && (
                                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                    <X size={20} />
                                </button>
                            )}
                        </div>
                        {isEditingTitle ? (
                            <div className="flex flex-col gap-2">
                                <input
                                    autoFocus
                                    className="w-full bg-white dark:bg-gray-800 border-2 border-brand-500 rounded-xl px-3 py-2 text-xl font-bold text-gray-900 dark:text-white outline-none"
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveTitle();
                                        if (e.key === 'Escape') setIsEditingTitle(false);
                                    }}
                                />
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => setIsEditingTitle(false)} className="px-3 py-1 text-xs font-bold text-gray-500 hover:text-gray-700">Cancel</button>
                                    <button 
                                        onClick={handleSaveTitle} 
                                        disabled={isSavingTitle}
                                        className="px-3 py-1 text-xs font-bold bg-brand-500 text-white rounded-lg shadow-md shadow-brand-500/20 flex items-center gap-2 min-w-[80px] justify-center"
                                    >
                                        {isSavingTitle ? <Loader2 size={12} className="animate-spin" /> : 'Save Title'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <h2 
                                onClick={() => {
                                    if (isEditAllowed) {
                                        setEditedTitle(task.title);
                                        setIsEditingTitle(true);
                                    }
                                }}
                                className={`text-xl font-bold text-gray-900 dark:text-white leading-tight break-words ${isEditAllowed ? 'cursor-edit hover:bg-gray-50 dark:hover:bg-gray-800/50 p-1 -m-1 rounded-lg transition-colors' : ''}`}
                            >
                                {task.title}
                            </h2>
                        )}
                    </div>

                    {/* Description Section */}
                    <div className="space-y-2">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Description</p>
                        {isEditingDesc ? (
                            <div className="space-y-2">
                                <textarea
                                    autoFocus
                                    className="w-full bg-white dark:bg-gray-800 border-2 border-brand-500 rounded-xl px-4 py-3 text-sm text-gray-700 dark:text-gray-300 outline-none min-h-[120px] resize-none leading-relaxed"
                                    value={editedDesc}
                                    onChange={(e) => setEditedDesc(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Escape') setIsEditingDesc(false);
                                    }}
                                />
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => setIsEditingDesc(false)} className="px-3 py-1 text-xs font-bold text-gray-500 hover:text-gray-700">Cancel</button>
                                    <button 
                                        onClick={handleSaveDesc} 
                                        disabled={isSavingDesc}
                                        className="px-3 py-1 text-xs font-bold bg-brand-500 text-white rounded-lg shadow-md shadow-brand-500/20 flex items-center gap-2 min-w-[110px] justify-center"
                                    >
                                        {isSavingDesc ? <Loader2 size={12} className="animate-spin" /> : 'Save Description'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div 
                                onClick={() => {
                                    if (isEditAllowed) {
                                        setEditedDesc(task.description || '');
                                        setIsEditingDesc(true);
                                    }
                                }}
                                className={`p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800 group/desc relative ${isEditAllowed ? 'cursor-edit hover:border-brand-200 dark:hover:border-brand-900/50 transition-all' : ''}`}
                            >
                                <p className={`text-sm leading-relaxed ${task.description ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 italic'}`}>
                                    {task.description || 'No description provided. Click to add one.'}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Meta Grid */}
                    <div className="grid grid-cols-2 gap-4 border-y border-gray-100 dark:border-gray-800 py-6 items-start">
                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Status</p>
                            {isEditAllowed ? (
                                <CustomSelect 
                                    options={statusOptions}
                                    value={task.status}
                                    onChange={handleStatusChange}
                                    disabled={isUpdatingStatus}
                                    renderOption={(opt) => (
                                        <div className="flex items-center gap-2">
                                            {opt.id === 'done' && <CheckCircle2 size={14} className="text-emerald-500" />}
                                            {opt.id === 'in-progress' && <Zap size={14} className="text-blue-500" />}
                                            {opt.id === 'deep-work' && <Zap size={14} className="text-purple-500 animate-pulse" />}
                                            {opt.id === 'blocked' && <AlertCircle size={14} className="text-red-500" />}
                                            {opt.id === 'todo' && <Clock size={14} className="text-gray-400" />}
                                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{opt.label}</span>
                                            {isUpdatingStatus && <Loader2 size={12} className="animate-spin ml-auto text-brand-500" />}
                                        </div>
                                    )}
                                />
                            ) : (
                                <div className="flex items-center h-10 px-1 gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {task.status === 'done' && <CheckCircle2 size={16} className="text-emerald-500" />}
                                    {task.status === 'in-progress' && <Zap size={16} className="text-blue-500" />}
                                    {task.status === 'deep-work' && <Zap size={16} className="text-purple-500 animate-pulse" />}
                                    {task.status === 'blocked' && <AlertCircle size={16} className="text-red-500" />}
                                    {task.status === 'todo' && <Clock size={16} className="text-gray-400" />}
                                    <span className="capitalize">{task.status.replace('-', ' ')}</span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Priority</p>
                            {isEditAllowed ? (
                                <CustomSelect 
                                    options={priorityOptions}
                                    value={task.priority}
                                    onChange={handlePriorityChange}
                                    disabled={isUpdatingPriority}
                                    renderOption={(opt) => (
                                        <div className="flex items-center gap-2">
                                            <Shield size={14} className={
                                                opt.id === 'critical' ? 'text-red-500' :
                                                opt.id === 'high' ? 'text-orange-500' :
                                                opt.id === 'medium' ? 'text-blue-500' : 'text-gray-400'
                                            } />
                                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{opt.label}</span>
                                            {isUpdatingPriority && <Loader2 size={12} className="animate-spin ml-auto text-brand-500" />}
                                        </div>
                                    )}
                                />
                            ) : (
                                <div className="flex items-center h-10 px-1 gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    <Shield size={16} className={
                                        task.priority === 'critical' ? 'text-red-500' :
                                        task.priority === 'high' ? 'text-orange-500' :
                                        task.priority === 'medium' ? 'text-blue-500' : 'text-gray-400'
                                    } />
                                    <span className="capitalize">{task.priority}</span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Assignee</p>
                            <div className="relative">
                                <CustomSelect 
                                    options={assigneeOptions}
                                    value={task.assigneeId || ''}
                                    onChange={handleAssigneeChange}
                                    disabled={isUpdatingAssignee}
                                    renderOption={(opt) => (
                                        <div className="flex items-center gap-2">
                                            <img src={opt.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(opt.label)}`} className="w-5 h-5 rounded-full object-cover" alt="" />
                                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{opt.label}</span>
                                            {isUpdatingAssignee && <Loader2 size={12} className="animate-spin ml-auto text-brand-500" />}
                                        </div>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Updated</p>
                            <div className="flex items-center h-10 gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                <Calendar size={16} className="text-gray-400" />
                                <span>{formatDateSafe(task.updatedAt, 'MMM d, h:mm a')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Dependencies Section */}
                    {(() => {
                        const deps = getDependencies(task);
                        if (deps.length === 0) return null;
                        return (
                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Waiting For</p>
                                <div className="flex flex-wrap gap-2">
                                    {deps.map(dep => (
                                        <div key={dep.id} className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded-lg border border-gray-100 dark:border-gray-800">
                                            {dep.status === 'done' ? (
                                                <CheckCircle2 size={12} className="text-emerald-500" />
                                            ) : (
                                                <Clock size={12} className="text-amber-500" />
                                            )}
                                            <span className={`text-xs font-semibold ${dep.status === 'done' ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {dep.title}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Blocked Reason Section */}
                    {task.status === 'blocked' && task.blockedReason && (
                        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-xl">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-widest mb-2">
                                <AlertCircle size={14} /> Blocker Reason
                            </div>
                            <p className="text-sm text-red-800 dark:text-red-200 italic leading-relaxed">
                                "{task.blockedReason}"
                            </p>
                        </div>
                    )}

                    {/* Discussion Section */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                            <MessageSquare size={14} /> Discussion
                        </div>
                        
                        <div className="space-y-3 pb-4">
                            {sortedComments.length > 0 ? (
                                sortedComments.map((comment) => (
                                    <div key={comment.id} className="group relative flex gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800">
                                        <img src={comment.userPhoto || `https://ui-avatars.com/api/?name=${comment.userName}`} className="w-8 h-8 rounded-lg object-cover shrink-0" alt="" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1 gap-2">
                                                <span className="text-xs font-bold text-gray-900 dark:text-white truncate">{comment.userName}</span>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className="text-[9px] text-gray-400">{formatDateSafe(comment.createdAt)}</span>
                                                    {(comment.userId === userProfile?.id || userProfile?.isOrgAdmin) && (
                                                        <button 
                                                            onClick={() => setCommentToDelete({taskId: task.id, commentId: comment.id})}
                                                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                                                            title="Delete note"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">{comment.text}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="py-8 text-center border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-2xl">
                                    <MessageSquare size={24} className="mx-auto mb-2 text-gray-300 dark:text-gray-700" />
                                    <p className="text-xs text-gray-400 italic">No notes yet. Add one below.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer / Input Area */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 shrink-0">
                <form onSubmit={handleAddComment} className="flex gap-2">
                    <input 
                        placeholder="Add a note..."
                        className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2 text-sm focus:ring-2 ring-brand-500 outline-none dark:text-white transition-all"
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                    />
                    <button 
                        type="submit"
                        disabled={!commentText.trim() || isSubmitting}
                        className="p-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl transition-all shadow-lg shadow-blue-500/20 active:scale-95"
                    >
                        {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                </form>
            </div>

            <AlertModal 
                isOpen={!!commentToDelete}
                title="Delete Note"
                message="Are you sure you want to delete this note? This action cannot be undone."
                confirmText="Delete Note"
                isDestructive={true}
                onConfirm={confirmDeleteComment}
                onCancel={() => setCommentToDelete(null)}
            />
        </div>
    );
};

export default TaskDetailView;
